import { useState, useRef, useCallback } from 'react'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	CircularProgress,
	Typography
} from '@mui/material'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import Resizer from 'react-image-file-resizer'

/**
 * Dialog component for cropping images with configurable aspect ratio and output size
 * 
 * @param {boolean} open - Dialog open state
 * @param {Function} onClose - Close handler
 * @param {File} imageFile - Image file to crop
 * @param {Function} onCropComplete - Callback with cropped image blob
 * @param {number} aspectRatio - Aspect ratio for crop (default: 1 for square)
 * @param {number} outputWidth - Output image width in pixels (default: 1000)
 * @param {number} outputHeight - Output image height in pixels (default: 1000)
 * @param {boolean} circularCrop - Whether to show circular crop preview (default: true)
 * @param {string} title - Dialog title (default: 'Crop Photo')
 */
function ImageCropDialog({
	open,
	onClose,
	imageFile,
	onCropComplete,
	aspectRatio = 1,
	outputWidth = 1000,
	outputHeight = 1000,
	circularCrop = true,
	title = 'Crop Photo'
}) {
	const [crop, setCrop] = useState()
	const [completedCrop, setCompletedCrop] = useState()
	const [imageSrc, setImageSrc] = useState('')
	const [processing, setProcessing] = useState(false)
	const imgRef = useRef(null)

	// Load image when file changes
	const handleImageLoad = useCallback((e) => {
		const { width, height } = e.currentTarget

		// Create initial centered crop with the specified aspect ratio
		// Use percentage units for UI stability
		const crop = centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: 90,
				},
				aspectRatio,
				width,
				height
			),
			width,
			height
		)
		setCrop(crop)
	}, [aspectRatio])

	// Read file and set image source when dialog opens
	useState(() => {
		if (imageFile && open) {
			const reader = new FileReader()
			reader.onload = () => {
				setImageSrc(reader.result)
			}
			reader.readAsDataURL(imageFile)
		}
	}, [imageFile, open])

	// Reset state when dialog opens with new file
	const handleDialogEnter = () => {
		if (imageFile) {
			const reader = new FileReader()
			reader.onload = () => {
				setImageSrc(reader.result)
			}
			reader.readAsDataURL(imageFile)
		}
		setCrop(undefined)
		setCompletedCrop(undefined)
		setProcessing(false)
	}

	const handleClose = () => {
		setImageSrc('')
		setCrop(undefined)
		setCompletedCrop(undefined)
		setProcessing(false)
		onClose()
	}

	// Get cropped image from canvas
	const getCroppedImage = useCallback(() => {
		return new Promise((resolve, reject) => {
			if (!completedCrop || !imgRef.current || !crop) {
				reject(new Error('No crop data'))
				return
			}

			const image = imgRef.current
			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')

			if (!ctx) {
				reject(new Error('No canvas context'))
				return
			}

			// Use the percentage crop to calculate source coordinates directly on natural image
			// This avoids issues with displayed vs natural dimension mismatches
			const naturalWidth = image.naturalWidth
			const naturalHeight = image.naturalHeight

			// crop is in percentage, convert to natural pixel coordinates
			const sourceX = (crop.x / 100) * naturalWidth
			const sourceY = (crop.y / 100) * naturalHeight
			const sourceWidth = (crop.width / 100) * naturalWidth
			const sourceHeight = (crop.height / 100) * naturalHeight

			// Set canvas to output dimensions
			canvas.width = outputWidth
			canvas.height = outputHeight

			// Fill with white background (JPEG doesn't support transparency)
			ctx.fillStyle = '#FFFFFF'
			ctx.fillRect(0, 0, outputWidth, outputHeight)

			// Draw the cropped area scaled to output size
			ctx.drawImage(
				image,
				sourceX,
				sourceY,
				sourceWidth,
				sourceHeight,
				0,
				0,
				outputWidth,
				outputHeight
			)

			// Convert to blob
			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve(blob)
					} else {
						reject(new Error('Failed to create blob'))
					}
				},
				'image/jpeg',
				0.9
			)
		})
	}, [crop, completedCrop, outputWidth, outputHeight])

	// Convert blob to JPEG using resizer (ensures format and quality)
	const convertToJpeg = useCallback((blob) => {
		return new Promise((resolve) => {
			// Create a File from blob for the resizer
			const file = new File([blob], 'image.jpg', { type: 'image/jpeg' })

			Resizer.imageFileResizer(
				file,
				outputWidth, // maxWidth
				outputHeight, // maxHeight
				'JPEG', // format
				90, // quality
				0, // rotation
				(uri) => {
					// Convert base64 to blob
					fetch(uri)
						.then(res => res.blob())
						.then(blob => resolve(blob))
				},
				'base64'
			)
		})
	}, [outputWidth, outputHeight])

	const handleConfirm = async () => {
		setProcessing(true)
		try {
			const croppedBlob = await getCroppedImage()
			const jpegBlob = await convertToJpeg(croppedBlob)
			onCropComplete(jpegBlob)
			handleClose()
		} catch (error) {
			console.error('Error cropping image:', error)
			setProcessing(false)
		}
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			TransitionProps={{
				onEnter: handleDialogEnter
			}}
		>
			<DialogTitle sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: '18px',
				color: '#2D3748'
			}}>
				{title}
			</DialogTitle>
			<DialogContent>
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 2,
					py: 2
				}}>
					{imageSrc ? (
						<>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
								Drag to adjust the crop area
							</Typography>
							<Box sx={{
								...(circularCrop && {
									'& .ReactCrop__crop-selection': {
										borderRadius: '50%'
									}
								})
							}}>
								<ReactCrop
									crop={crop}
									onChange={(_, percentCrop) => setCrop(percentCrop)}
									onComplete={(c) => setCompletedCrop(c)}
									aspect={aspectRatio}
									circularCrop={circularCrop}
								>
									<img
										ref={imgRef}
										src={imageSrc}
										alt="Crop preview"
										onLoad={handleImageLoad}
										style={{
											display: 'block',
											maxWidth: '100%',
											maxHeight: '400px'
										}}
									/>
								</ReactCrop>
							</Box>
						</>
					) : (
						<Box sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: 200
						}}>
							<CircularProgress size={40} />
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ p: 2 }}>
				<Button
					onClick={handleClose}
					disabled={processing}
					sx={{
						color: '#4A5568',
						textTransform: 'none',
						fontFamily: 'Inter',
						fontWeight: 500
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={!completedCrop || processing}
					variant="contained"
					sx={{
						bgcolor: '#9F7AEA',
						'&:hover': { bgcolor: '#805AD5' },
						textTransform: 'none',
						fontFamily: 'Inter',
						fontWeight: 600
					}}
				>
					{processing ? (
						<CircularProgress size={20} sx={{ color: 'white' }} />
					) : (
						'Save'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default ImageCropDialog
